import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { imagesDisplay } from 'hb-react/frontoffice/apps/LiveFeed/utils/attachedImages';

import { AttachedImage } from './AttachedImage';
import AttachedImagePreviewModal from './AttachedImagePreviewModal';

const AttachedImages = ({
  images = [],
  canDisplay,
  categories,
  owner,
  user,
  createdAt,
  content,
  sharePostUrl,
  ...modalProps
}) => {
  const breakpoint = useBreakpoint();
  const isMobile = useIsMobileDevice();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);

  const selectedIndex = useMemo(
    () => images.findIndex((image) => image.id === selectedFileId),
    [images, selectedFileId],
  );
  const display = useMemo(() => imagesDisplay(images), [images]);
  const moreImagesCount = useMemo(() => images.length - 6, [images]);
  const getImageIndex = useCallback(
    (id) => images.findIndex((image) => image.id === id),
    [images],
  );
  const displayMoreImages = useCallback(
    (imageId) => getImageIndex(imageId) === 5 && images.length > 6,
    [images, getImageIndex],
  );

  const isOnlyOneImage = images.length === 1;
  const marginX = isMobile ? 'auto' : 0;

  const handleOpenModal = useCallback(
    (id) => {
      setIsOpen(true);
      setSelectedFileId(id);
    },
    [setIsOpen, setSelectedFileId],
  );
  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    setSelectedFileId(null);
  }, [setIsOpen, setSelectedFileId]);

  const handleNextImage =
    images.length > 1
      ? () => {
          setSelectedFileId(images[selectedIndex + 1]?.id ?? images[0].id);
        }
      : null;
  const handlePreviousImage =
    images.length > 1
      ? () => {
          setSelectedFileId(
            images[selectedIndex - 1]?.id ?? images[images.length - 1].id,
          );
        }
      : null;

  const file = useMemo(
    () => ({
      content_type: 'image/jpeg',
      created_at: createdAt,
      extension: null,
      file_type: 'image',
      large_url:
        images[selectedIndex]?.large_url ?? images[selectedIndex]?.default_url,
      name: images[selectedIndex]?.name,
      network_category_ids: categories.map((element) => element.id),
      owner,
      sharePostUrl,
      user,
    }),
    [categories, createdAt, images, owner, selectedIndex, sharePostUrl, user],
  );

  const HEIGHT_BREAKPOINTS = {
    l: 400,
    m: 400,
    s: 300,
    xl: 600,
  };

  const maxHeight = HEIGHT_BREAKPOINTS[breakpoint] || 400;

  const isSingle = images.length === 1;

  return (
    <ImagesWrapper data-testid="attached-images">
      {display.map(({ images: lineImages, height }) => (
        <LineWrapper
          height={isSingle ? 'auto' : height}
          key={`line-wrapper-${lineImages?.[0]?.id}`}
          maxHeight={isSingle ? maxHeight : height}
        >
          {lineImages.map((image) => (
            <AttachedImage
              breakpoint={breakpoint}
              canDisplay={canDisplay}
              displayMoreImages={displayMoreImages(image.id)}
              height={height}
              image={image}
              isSingle={isSingle}
              key={image.id}
              marginX={marginX}
              maxHeight={maxHeight}
              moreImagesCount={moreImagesCount}
              onClick={handleOpenModal}
            />
          ))}
        </LineWrapper>
      ))}
      {isOpen && (
        <AttachedImagePreviewModal
          isOpen={isOpen}
          image={images[selectedIndex]}
          onCloseModal={handleCloseModal}
          categories={categories}
          user={user}
          file={file}
          onNext={handleNextImage}
          onPrevious={handlePreviousImage}
          hideNextUrl={isOnlyOneImage}
          hidePreviousUrl={isOnlyOneImage}
          postContent={content}
          {...modalProps}
        />
      )}
    </ImagesWrapper>
  );
};

export default AttachedImages;

const LineWrapper = styled(Box).attrs(() => ({
  mt: 2,
  width: '100%',
}))`
  > * + * {
    margin-left: ${(props) => props.theme.space[2]}px;
  }
`;

const ImagesWrapper = styled(Box).attrs(() => ({
  mx: { _: -4, m: 0 },
  display: 'block',
}))``;

AttachedImages.propTypes = {
  images: PropTypes.array,
};
